/**
 * Shown when idle timeout is ≤120s away. Focus-trapped modal with countdown.
 */

import { useEffect, useRef } from "react";

interface Props {
  secondsRemaining: number;
  onStaySignedIn: () => void;
  onSignOut: () => void;
}

export function WarningModal({ secondsRemaining, onStaySignedIn, onSignOut }: Props) {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  // Trap focus inside modal
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const focusable = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const mins = Math.floor(secondsRemaining / 60);
  const secs = secondsRemaining % 60;
  const countdown =
    mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onKeyDown={handleKeyDown}
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2
          id="session-warning-title"
          className="text-lg font-semibold text-gray-900"
        >
          Your session is about to expire
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          You'll be signed out in{" "}
          <span className="font-mono font-bold text-amber-600">{countdown}</span>{" "}
          due to inactivity. Click below to stay signed in.
        </p>
        <div className="mt-6 flex gap-3">
          <button
            ref={primaryRef}
            onClick={onStaySignedIn}
            className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Stay signed in
          </button>
          <button
            onClick={onSignOut}
            className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
